import React from "react";
import { CCard, CCardBody, CCardHeader } from "@coreui/react";
import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import { Group as GroupIcon } from "@mui/icons-material";
import { useMyGroups } from "src/views/admin/groups/groupService";

/**
 * Read-only "my groups" view (spec §5.8): which groups the current user
 * belongs to. Groups are admin-managed; membership changes go through an
 * admin, so there are no actions here.
 */
const MyGroupsCard: React.FC = () => {
  const { data: groups, isLoading, isError, refetch } = useMyGroups();

  return (
    <CCard data-testid="uprof-card-my-groups">
      <CCardHeader>
        <h4>My Groups</h4>
        <small className="text-muted">
          Groups you belong to. Group membership is managed by administrators.
        </small>
      </CCardHeader>
      <CCardBody>
        {isLoading ? (
          <Stack spacing={1} data-testid="my-groups-skeleton">
            <Skeleton variant="rounded" height={28} />
            <Skeleton variant="rounded" height={28} />
          </Stack>
        ) : isError ? (
          <Stack spacing={1} alignItems="flex-start">
            <Typography variant="body2" color="error">
              Failed to load your groups.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => refetch()}
              data-testid="my-groups-retry-btn"
            >
              Retry
            </Button>
          </Stack>
        ) : !groups || groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            You are not a member of any groups.
          </Typography>
        ) : (
          <Stack spacing={1} data-testid="my-groups-list">
            {groups.map((group) => (
              <Box
                key={group.id}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                data-testid={`my-group-${group.id}`}
              >
                <GroupIcon fontSize="small" color="primary" />
                <Typography variant="body2">{group.name}</Typography>
                {group.description && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {group.description}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </CCardBody>
    </CCard>
  );
};

export default MyGroupsCard;
